import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const PAGE_SIZE = 10;

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: 'M12 5v14 M5 12h14',
    search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
    x: 'M18 6L6 18 M6 6l12 12',
    truck: 'M1 3h15v13H1V3zm15 8h4l3 3v2h-7v-5zM5 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm12 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    building: 'M3 21h18M3 7v14m18-14v14M3 7l9-4 9 4M5 21V11m4 10V11m6 10V11m4 10V11'
};

const defaultVehicle = {
    vendorId: '',
    vehicleType: '',
    registrationNumber: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    fuelType: '',
    color: '',
    purchasePrice: '',
    acquisitionDate: '',
    depreciationMonthly: '',
    gpsMonthly: '',
    dashcamMonthly: '',
    insuranceMonthly: '',
    otherCostsMonthly: '',
    status: 'Active'
};

export default function VendorFleet({ user }) {
    const [fleet, setFleet] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [vendorFilter, setVendorFilter] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(defaultVehicle);
    const [editingId, setEditingId] = useState(null);
    const [toast, setToast] = useState(null);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchFleet = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/vendor-fleet/${user?.operatorId || 'all'}`);
            setFleet(res.data);
        } catch (err) {
            console.error('Fetch fleet error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchVendors = async () => {
        try {
            const res = await axios.get(`/api/vendors/${user?.operatorId || 'all'}`);
            setVendors(res.data);
        } catch (err) {
            console.error('Fetch vendors error:', err);
        }
    };

    const fetchVehicleTypes = async () => {
        try {
            const res = await axios.get('/api/master-data');
            const types = res.data.filter(i => i.category === 'vehicle-types' && i.active);
            setVehicleTypes(types);
        } catch (err) {
            console.error('Fetch vehicle types error:', err);
        }
    };

    useEffect(() => {
        fetchFleet();
        fetchVendors();
        fetchVehicleTypes();
    }, [user]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return fleet.filter(f => {
            const vendorMatch = !vendorFilter || (f.vendorId?._id || f.vendorId) === vendorFilter;
            const searchMatch = !q ||
                (f.vendorId?.vendorName || '').toLowerCase().includes(q) ||
                f.registrationNumber.toLowerCase().includes(q) ||
                f.make.toLowerCase().includes(q) ||
                f.model.toLowerCase().includes(q) ||
                f.vehicleType.toLowerCase().includes(q);
            return vendorMatch && searchMatch;
        });
    }, [fleet, search, vendorFilter]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form, operatorId: user?.operatorId || user?._id };
            if (editingId) {
                await axios.put(`/api/vendor-fleet/${editingId}`, payload);
                showToast('Vehicle updated successfully');
            } else {
                await axios.post('/api/vendor-fleet', payload);
                showToast('Vehicle added successfully');
            }
            fetchFleet();
            closeModal();
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to save vehicle';
            showToast(msg, 'error');
        }
    };

    const handleDelete = async (vehicle) => {
        if (!window.confirm(`Delete vehicle "${vehicle.registrationNumber}"?`)) return;
        try {
            await axios.delete(`/api/vendor-fleet/${vehicle._id}`);
            showToast('Vehicle deleted successfully');
            fetchFleet();
        } catch (err) {
            showToast('Failed to delete vehicle', 'error');
        }
    };

    const openModal = (vehicle = null) => {
        if (vehicle) {
            setEditingId(vehicle._id);
            setForm({
                vendorId: vehicle.vendorId?._id || vehicle.vendorId || '',
                vehicleType: vehicle.vehicleType || '',
                registrationNumber: vehicle.registrationNumber || '',
                make: vehicle.make || '',
                model: vehicle.model || '',
                year: vehicle.year || new Date().getFullYear(),
                fuelType: vehicle.fuelType || '',
                color: vehicle.color || '',
                purchasePrice: vehicle.purchasePrice || '',
                acquisitionDate: vehicle.acquisitionDate ? vehicle.acquisitionDate.slice(0, 10) : '',
                depreciationMonthly: vehicle.depreciationMonthly || '',
                gpsMonthly: vehicle.gpsMonthly || '',
                dashcamMonthly: vehicle.dashcamMonthly || '',
                insuranceMonthly: vehicle.insuranceMonthly || '',
                otherCostsMonthly: vehicle.otherCostsMonthly || '',
                status: vehicle.status || 'Active'
            });
        } else {
            setEditingId(null);
            setForm(defaultVehicle);
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm(defaultVehicle);
    };

    return (
        <div className="fleet-page">
            <style>{`
                .fleet-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .header-section { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
                .title-wrap h1 { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }
                .add-btn { 
                    background: #6366f1; color: white; border: none; padding: 10px 20px; 
                    border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    transition: all 0.2s; box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
                }
                .add-btn:hover { background: #4f46e5; transform: translateY(-1px); }

                .vendor-filter-bar {
                    display: flex; align-items: center; gap: 14px; margin-bottom: 20px;
                    padding: 14px 20px; background: white; border-radius: 12px;
                    border: 1px solid #e2e8f0; box-shadow: 0 1px 4px rgba(0,0,0,0.03);
                }
                .vendor-filter-bar label {
                    font-size: 13px; font-weight: 600; color: #475569; white-space: nowrap;
                }
                .vendor-filter-select {
                    padding: 9px 14px; border: 1.5px solid #e2e8f0; border-radius: 8px;
                    font-size: 14px; color: #1e293b; background: #f8fafc; outline: none;
                    min-width: 240px; cursor: pointer; transition: all 0.2s;
                    font-family: 'Inter', sans-serif;
                }
                .vendor-filter-select:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }

                .content-box { 
                    background: white; border-radius: 16px; border: 1px solid #e2e8f0; 
                    box-shadow: 0 4px 20px rgba(0,0,0,0.03); padding: 24px;
                }
                
                .table-container { overflow-x: auto; }
                .fleet-table { width: 100%; border-collapse: collapse; }
                .fleet-table th { 
                    padding: 14px 20px; text-align: left; font-size: 12px; font-weight: 600; 
                    color: #64748b; text-transform: uppercase; letter-spacing: 0.05em;
                    border-bottom: 1px solid #e2e8f0; background: #fff;
                }
                .fleet-table td { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
                .fleet-table tr:hover { background: #f8fafc; }
                
                .vendor-text { font-weight: 600; color: #1e293b; }
                .vehicle-info { display: flex; flex-direction: column; }
                .vehicle-main { font-weight: 600; color: #334155; }
                .vehicle-sub { font-size: 13px; color: #64748b; }

                .status-badge { 
                    padding: 5px 15px; border-radius: 99px; font-size: 12px; font-weight: 600;
                    display: inline-block;
                }
                .status-active { background: #f0fdf4; color: #166534; border: 1px solid #dcfce7; }
                .status-under-review { background: #fef9c3; color: #92400e; border: 1px solid #fef08a; }
                .status-inactive { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
                
                .actions-cell { display: flex; gap: 8px; }
                .act-btn { 
                    border: none; background: none; padding: 6px; cursor: pointer; border-radius: 6px;
                    color: #94a3b8; transition: all 0.2s;
                }
                .act-btn:hover { background: #f1f5f9; color: #6366f1; }
                .act-btn.delete:hover { background: #fee2e2; color: #ef4444; }

                /* Modal */
                .modal-overlay { 
                    position: fixed; inset: 0; background: rgba(15, 23, 42, 0.6); 
                    backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000;
                    animation: fadeIn 0.3s ease;
                }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

                .modal-content { 
                    background: white; border-radius: 28px; width: 100%; max-width: 680px; 
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                    overflow: hidden; border: 1px solid rgba(0, 0, 0, 0.05);
                    max-height: 92vh; display: flex; flex-direction: column;
                }
                @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

                .modal-header { 
                    padding: 24px 32px; border-bottom: 1px solid #f1f5f9; 
                    display: flex; justify-content: space-between; align-items: center;
                    background: #fff;
                }
                .modal-header h2 { font-size: 20px; font-weight: 800; color: #1e293b; margin: 0; letter-spacing: -0.02em; }
                .close-btn { 
                    border: none; background: #f8fafc; cursor: pointer; color: #64748b; padding: 8px; 
                    border-radius: 10px; transition: all 0.2s;
                }
                .close-btn:hover { color: #1e1b4b; background: #f1f5f9; transform: rotate(90deg); }

                .modal-body { padding: 32px; background: #fff; overflow-y: auto; flex: 1; }
                .section-label { font-size: 11px; font-weight: 800; color: #6366f1; text-transform: uppercase; letter-spacing: 0.08em; margin: 24px 0 14px; padding-bottom: 6px; border-bottom: 1.5px solid #e0e7ff; }
                .section-label:first-child { margin-top: 0; }
                
                .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                .form-group { display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; }
                .form-group.full { grid-column: span 2; }
                .form-label { font-size: 13px; font-weight: 700; color: #475569; display: flex; align-items: center; gap: 6px; }
                
                .input-wrapper { position: relative; display: flex; align-items: center; }
                .input-icon { position: absolute; left: 14px; color: #94a3b8; pointer-events: none; }
                
                .form-input { 
                    width: 100%; padding: 12px 16px 12px 42px; border: 2px solid #e2e8f0; border-radius: 14px; font-size: 14px; 
                    background: #fcfdfe; outline: none; transition: all 0.2s; font-family: 'Inter', sans-serif;
                    color: #1e293b; box-sizing: border-box; font-weight: 500;
                }
                .form-input:focus { 
                    border-color: #6366f1; box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.08); 
                    background: #fff;
                }
                .form-input:focus + .input-icon { color: #6366f1; }

                .modal-footer { 
                    padding: 24px 32px; border-top: 1px solid #f1f5f9; 
                    display: flex; justify-content: flex-end; gap: 14px; 
                    background: #fcfdfe;
                }
                .cancel-btn { 
                    padding: 12px 24px; border: 2px solid #e2e8f0; background: white; border-radius: 12px;
                    font-size: 14px; font-weight: 700; color: #64748b; cursor: pointer; transition: all 0.2s;
                }
                .cancel-btn:hover { border-color: #6366f1; color: #6366f1; background: #f5f3ff; }
                
                .submit-btn { 
                    padding: 12px 28px; border: none; background: #6366f1; color: white; border-radius: 12px;
                    font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.2s;
                    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
                }
                .submit-btn:hover { background: #4f46e5; transform: translateY(-2px); box-shadow: 0 8px 20px rgba(99, 102, 241, 0.3); }

                .toast {
                    position: fixed; top: 24px; right: 24px; padding: 16px 28px; border-radius: 16px;
                    color: white; z-index: 2000; box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    font-weight: 700; font-size: 14px; animation: toastSlide 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                }
                @keyframes toastSlide { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
                .toast.success { background: #10b981; }
                .toast.error { background: #ef4444; }
            `}</style>

            {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

            <div className="header-section">
                <div className="title-wrap">
                    <h1>Vendor Fleet</h1>
                </div>
                <button className="add-btn" onClick={() => openModal()}>
                    <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                    Add Vehicle
                </button>
            </div>

            <div className="vendor-filter-bar">
                <label>Filter by Vendor:</label>
                <select
                    className="vendor-filter-select"
                    value={vendorFilter}
                    onChange={e => { setVendorFilter(e.target.value); setPage(1); }}
                >
                    <option value="">All Vendors</option>
                    {vendors.map(v => (
                        <option key={v._id} value={v._id}>{v.vendorName}</option>
                    ))}
                </select>
            </div>

            <div className="content-box">
                <div className="table-container">
                    <table className="fleet-table">
                        <thead>
                            <tr>
                                <th>Vendor</th>
                                <th>Vehicle Info</th>
                                <th>Registration</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5" style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Loading fleet...</td></tr>
                            ) : pageData.length === 0 ? (
                                <tr><td colSpan="5" style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>No vehicles found.</td></tr>
                            ) : (
                                pageData.map(item => (
                                    <tr key={item._id}>
                                        <td>
                                            <div className="vendor-text">{item.vendorId?.vendorName || 'Deleted Vendor'}</div>
                                        </td>
                                        <td>
                                            <div className="vehicle-info">
                                                <span className="vehicle-main">{item.make} {item.model}</span>
                                                <span className="vehicle-sub">({item.vehicleType})</span>
                                            </div>
                                        </td>
                                        <td style={{ fontWeight: 500, color: '#334155' }}>
                                            {item.registrationNumber}
                                        </td>
                                        <td>
                                            <span className={`status-badge status-${item.status?.toLowerCase().replace(' ', '-')}`}>
                                                {item.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="actions-cell">
                                                <button className="act-btn" title="Edit" onClick={() => openModal(item)}>
                                                    <Icon d={icons.edit} size={16} />
                                                </button>
                                                <button className="act-btn delete" title="Delete" onClick={() => handleDelete(item)}>
                                                    <Icon d={icons.trash} size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingId ? 'Edit Vehicle Details' : 'Register New Vehicle'}</h2>
                            <button className="close-btn" onClick={closeModal}><Icon d={icons.x} size={20} /></button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="modal-body">
                                {/* ── Section: Vendor & Identity ── */}
                                <div className="section-label">Vendor & Identification</div>
                                <div className="form-grid">
                                    <div className="form-group full">
                                        <label className="form-label">
                                            <Icon d={icons.building} size={14} />
                                            Vendor *
                                        </label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.building} size={16} className="input-icon" />
                                            <select className="form-input" required value={form.vendorId} onChange={e => setForm({ ...form, vendorId: e.target.value })}>
                                                <option value="">Select a vendor</option>
                                                {vendors.map(v => (
                                                    <option key={v._id} value={v._id}>{v.vendorName}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Registration Number *</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.search} size={16} className="input-icon" />
                                            <input className="form-input" required placeholder="e.g. DXB-12345" value={form.registrationNumber} onChange={e => setForm({ ...form, registrationNumber: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Vehicle Type *</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" />
                                            <select className="form-input" required value={form.vehicleType} onChange={e => setForm({ ...form, vehicleType: e.target.value })}>
                                                <option value="">Select type</option>
                                                {vehicleTypes.map(vt => (
                                                    <option key={vt._id} value={vt.name}>{vt.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Section: Vehicle Details ── */}
                                <div className="section-label">Vehicle Details</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Make *</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" style={{ opacity: 0.5 }} />
                                            <input className="form-input" required placeholder="e.g. Toyota" value={form.make} onChange={e => setForm({ ...form, make: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Model</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" style={{ opacity: 0.5 }} />
                                            <input className="form-input" placeholder="e.g. Hiace" value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Year</label>
                                        <div className="input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="input-icon" />
                                            <input className="form-input" type="number" min="1990" max={new Date().getFullYear() + 1} placeholder={new Date().getFullYear()} value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Fuel Type</label>
                                        <div className="input-wrapper">
                                            <Icon d="M13 2a2 2 0 0 0-2 2v1H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4V4a2 2 0 0 0-2-2z" size={16} className="input-icon" />
                                            <select className="form-input" value={form.fuelType} onChange={e => setForm({ ...form, fuelType: e.target.value })}>
                                                <option value="">Select fuel type</option>
                                                <option>Petrol</option>
                                                <option>Diesel</option>
                                                <option>Electric</option>
                                                <option>Hybrid</option>
                                                <option>CNG</option>
                                                <option>LPG</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Color</label>
                                        <div className="input-wrapper" style={{ gap: 10 }}>
                                            <input type="color" value={form.color || '#ffffff'} onChange={e => setForm({ ...form, color: e.target.value })} style={{ width: 42, height: 42, border: '2px solid #e2e8f0', borderRadius: 10, cursor: 'pointer', padding: 2, background: 'none' }} />
                                            <input className="form-input" style={{ paddingLeft: 16 }} placeholder="e.g. White" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Operational Status</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" style={{ color: form.status === 'Active' ? '#10b981' : '#94a3b8' }} />
                                            <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                                <option value="Under Review">Under Review</option>
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                                <option value="Under Maintenance">Under Maintenance</option>
                                                <option value="Damaged">Damaged</option>
                                                <option value="Retired">Retired</option>
                                                <option value="In Transit">In Transit</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Section: Financial Details ── */}
                                <div className="section-label">Financial Details</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Purchase Price</label>
                                        <div className="input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="input-icon" />
                                            <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.purchasePrice} onChange={e => setForm({ ...form, purchasePrice: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Acquisition Date</label>
                                        <div className="input-wrapper">
                                            <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={16} className="input-icon" />
                                            <input className="form-input" type="date" value={form.acquisitionDate} onChange={e => setForm({ ...form, acquisitionDate: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Depreciation / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="input-icon" />
                                            <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.depreciationMonthly} onChange={e => setForm({ ...form, depreciationMonthly: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">GPS Fee / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="input-icon" />
                                            <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.gpsMonthly} onChange={e => setForm({ ...form, gpsMonthly: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Dashcam Fee / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="input-icon" />
                                            <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.dashcamMonthly} onChange={e => setForm({ ...form, dashcamMonthly: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Insurance / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="input-icon" />
                                            <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.insuranceMonthly} onChange={e => setForm({ ...form, insuranceMonthly: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Other Costs / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" size={16} className="input-icon" />
                                            <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.otherCostsMonthly} onChange={e => setForm({ ...form, otherCostsMonthly: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Monthly Total</label>
                                        <div style={{ padding: '12px 16px', borderRadius: 14, background: 'linear-gradient(135deg,#eef2ff,#e0e7ff)', border: '1.5px solid #c7d2fe', fontWeight: 800, fontSize: 17, color: '#4338ca', textAlign: 'center' }}>
                                            {(
                                                (parseFloat(form.depreciationMonthly) || 0) +
                                                (parseFloat(form.gpsMonthly) || 0) +
                                                (parseFloat(form.dashcamMonthly) || 0) +
                                                (parseFloat(form.insuranceMonthly) || 0) +
                                                (parseFloat(form.otherCostsMonthly) || 0)
                                            ).toFixed(2)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="cancel-btn" onClick={closeModal}>Discard</button>
                                <button type="submit" className="submit-btn">{editingId ? 'Update Vehicle' : 'Register Vehicle'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
